<template>
  <div class="sc_search_select" v-click-outside="turnOffSelect">
    <div class="group_search_select">
      <button
        type="button"
        class="button_drop_search"
        :class="[{ active: isShow }]"
        @click="toggleSelect()"
        @keydown="onTriggerKeydown"
        :aria-expanded="isShow ? 'true' : 'false'"
        aria-haspopup="listbox"
        ref="trigger"
      >
        <slot name="label_selected" v-if="customLabel" :selected="value"></slot>
        <span v-else v-html="label"></span>
        <i class="icon-arrow1"></i>
      </button>

      <div class="box_list_search_select active" v-show="isShow">
        <ul
          class="list_search_select"
          role="listbox"
          :aria-activedescendant="activeDescId"
        >
          <li
            v-for="(option, idx) in options"
            :key="optionKey(option, idx)"
            role="none"
          >
            <button
              type="button"
              class="full_name_coin_select"
              role="option"
              :id="optionId(idx)"
              :aria-selected="idx === focusedIndex ? 'true' : 'false'"
              @click="select(option)"
              @keydown="onOptionKeydown($event, idx)"
              ref="opt"
            >
              <slot v-if="customLabel" name="option" :option="option"></slot>
              <span v-else v-html="getSelectedValue(option)"></span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    placeholder: { type: String },
    customLabel: { type: Boolean, default: false },
    options: { type: Array, default: () => [] },
    value: { default: "" },
  },
  data() {
    return {
      isShow: false,
      focusedIndex: -1,
    };
  },
  computed: {
    label() {
      const selected = this.getSelectedValue(this.value);
      return selected || this.placeholder;
    },
    activeDescId() {
      return this.focusedIndex >= 0 ? this.optionId(this.focusedIndex) : null;
    },
  },
  methods: {
    optionKey(option, idx) {
      if (window._ && window._.isObject(option))
        return option.id != null ? option.id : idx;
      return `${option}-${idx}`;
    },
    optionId(idx) {
      return `ss_opt_${idx}`;
    },
    toggleSelect() {
      const next = !this.isShow;
      this.isShow = next;
      this.$nextTick(() => {
        if (next) this.focusInitial();
      });
    },
    turnOffSelect() {
      this.isShow = false;
      this.focusedIndex = -1;
    },
    select(option) {
      this.$emit("input", option);
      this.turnOffSelect();
      this.$nextTick(() => {
        if (this.$refs.trigger && this.$refs.trigger.focus) {
          this.$refs.trigger.focus();
        }
      });
    },
    getSelectedValue(option) {
      const selected = this.options.find((item) => {
        if (window._ && window._.isObject(option)) return item.id === option.id;
        return item === option;
      });
      return window._ && window._.isObject(option)
        ? selected && selected.name
        : selected;
    },
    equalsOption(a, b) {
      if (window._ && window._.isObject(a) && window._.isObject(b))
        return a.id === b.id;
      return a === b;
    },
    focusInitial() {
      let idx = this.options.findIndex((o) => this.equalsOption(o, this.value));
      if (idx < 0) idx = this.options.length ? 0 : -1;
      this.setFocus(idx);
    },
    moveFocus(delta) {
      if (!this.options.length) return;
      let idx = this.focusedIndex;
      if (idx < 0) idx = 0;
      idx = (idx + delta + this.options.length) % this.options.length;
      this.setFocus(idx);
    },
    setFocus(idx, silent) {
      this.focusedIndex = idx;
      if (idx < 0) return;
      this.$nextTick(() => {
        const btns = this.$refs.opt || [];
        const el = Array.isArray(btns) ? btns[idx] : btns;
        if (el && el.focus && !silent) el.focus();
      });
    },
    onTriggerKeydown(e) {
      const k = e.key;
      if (k === "ArrowDown" || k === "Down") {
        e.preventDefault();
        if (!this.isShow) {
          this.isShow = true;
          this.$nextTick(() => this.focusInitial());
        } else {
          this.moveFocus(1);
        }
      } else if (k === "ArrowUp" || k === "Up") {
        e.preventDefault();
        if (!this.isShow) {
          this.isShow = true;
          this.$nextTick(() => this.focusInitial());
        } else {
          this.moveFocus(-1);
        }
      } else if (k === "Enter" || k === " ") {
        e.preventDefault();
        this.toggleSelect();
      } else if (k === "Escape" || k === "Esc") {
        if (this.isShow) {
          e.preventDefault();
          this.turnOffSelect();
        }
      }
    },
    onOptionKeydown(e, idx) {
      const k = e.key;
      if (k === "ArrowDown" || k === "Down") {
        e.preventDefault();
        this.moveFocus(1);
      } else if (k === "ArrowUp" || k === "Up") {
        e.preventDefault();
        this.moveFocus(-1);
      } else if (k === "Home") {
        e.preventDefault();
        this.setFocus(0);
      } else if (k === "End") {
        e.preventDefault();
        this.setFocus(this.options.length - 1);
      } else if (k === "Enter" || k === " ") {
        e.preventDefault();
        const opt = this.options[idx];
        if (opt) this.select(opt);
      } else if (k === "Escape" || k === "Esc") {
        e.preventDefault();
        this.turnOffSelect();
        this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus());
      } else if (k === "Tab") {
        this.turnOffSelect();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass/variables";
.pr-5 {
  padding-right: 5px;
}
::-webkit-scrollbar {
  width: 5px;
}

.icon-class {
  .sc_search_select {
    .group_search_select {
      .box_list_search_select {
        min-width: 150px;
        left: 50%;
        transform: translateX(-50%);
        .list_search_select {
          max-height: 150px;
          overflow-y: auto;
        }
      }
    }
  }
}
.icon-type {
  .sc_search_select {
    .group_search_select {
      .box_list_search_select {
        .list_search_select {
          max-height: 150px;
          overflow-y: auto;
        }
      }
    }
  }
}

.sc_search_select {
  .tit_search_select {
    font-size: $font-title-size-big;
    color: $color-white;
    margin-bottom: 20px;
    line-height: 44px;
  }
  .group_search_select {
    position: relative;

    .button_drop_search {
      cursor: pointer;
      height: 30px;
      overflow: hidden;
      line-height: 18px;
      padding: 5px 10px;
      border: 1px solid $color-alto;
      border-radius: 3px;
      background: #fff;
      appearance: none;

      span {
        display: block;
        float: left;
        color: $color-grey-dusty;
        font-size: 13px;
        line-height: 18px;
      }
      .icon-arrow1 {
        padding: 4px 0px;
        font-size: 11px;
        float: right;
        transition: transform 0.2s ease;
      }
      &.active {
        display: block;
        visibility: visible;
        opacity: 1;
        .icon-arrow1 {
          transform: rotate(180deg);
        }
      }
      &:focus-visible {
        outline: 2px solid $color-jungle-green;
        outline-offset: 2px;
        box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.2);
      }
      &:focus:not(:focus-visible) {
        outline: none;
      }
      &[aria-expanded="true"] {
        border-color: $color-jungle-green;
      }
    }

    .box_list_search_select {
      padding-top: 5px;
      /* position: absolute; */
      display: none;
      width: 100%;
      top: 100%;
      left: 0px;
      z-index: 111;
      float: left;
      visibility: hidden;
      opacity: 0;
      max-height: 470px;
      ul {
        margin: 0;
      }
      &.active {
        display: block;
        visibility: visible;
        opacity: 1;
      }
    }

    .list_search_select {
      width: 55px;
      background: $background-default;
      box-shadow: 0px 3px 12px rgba(49, 49, 49, 0.51);
      list-style-type: none;
      max-height: auto;
      position: relative;
      padding: 12px 0px;

      &:after {
        bottom: 100%;
        left: 50%;
        border: solid transparent;
        content: " ";
        display: block;
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border-color: rgba(136, 183, 213, 0);
        border-bottom-color: $color-white;
        border-width: 5px;
        margin-left: -5px;
      }

      li {
        display: block;
        width: 100%;
        line-height: 20px;
        cursor: pointer;
        padding: 0;
        font-size: 14px;
        overflow: hidden;

        > button.full_name_coin_select {
          display: block;
          width: 100%;
          text-align: left;
          background: transparent;
          border: 0;
          padding: 3px 14px;
          font: inherit;
          cursor: pointer;
          transition: background-color 0.12s ease, box-shadow 0.12s ease;

          &:hover {
            background: rgba(0, 0, 0, 0.03);
          }
          &:focus-visible {
            box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.2);
          }
          &:focus:not(:focus-visible) {
            outline: none;
          }
          &:active {
            background: rgba($color-jungle-green, 0.16);
          }
          &[aria-selected="true"] {
            background: rgba($color-jungle-green, 0.14);
            font-weight: 600;
          }
        }
      }
    }
  }
}
</style>
